/*
 * Slug generation and validation.
 *
 * The database is the authority here. kb.Article, kb.Product and kb.Category
 * all carry
 *
 *     CHECK (LEN(Slug) > 0 AND Slug COLLATE Latin1_General_100_CS_AS
 *            NOT LIKE '%[^a-z0-9-]%')
 *
 * so a slug is lowercase ASCII letters, digits and hyphens, and nothing else.
 * The collation is explicit in the constraint because the database default is
 * case-insensitive, under which [^a-z0-9-] would also accept A-Z. That means
 * "Getting-Started" is rejected by the database, not silently lowercased - so
 * the client must produce a conforming slug rather than hope.
 */
export const SLUG_PATTERN = /^[a-z0-9-]+$/;
export const SLUG_MAX_LENGTH = 200;
/** kb.Article.Slug is nvarchar(300); the catalogues are nvarchar(200). */
export const ARTICLE_SLUG_MAX_LENGTH = 300;
/**
 * Characters that must not simply lose their diacritic. Stripping the ring off
 * "aa" gives "a", which collides with unrelated words; the conventional ASCII
 * spellings are these.
 *
 * This table is about the CHARACTERS an author can type, not about any language
 * the knowledge base is written in. A title is free text - a product name, a
 * customer name, a place name, a borrowed word - and any of those can carry a
 * letter outside a-z while the slug may not. Without the table the slug
 * generator would silently fold distinct titles onto the same slug and then
 * collide with the UNIQUE constraint, or produce something an author does not
 * recognise as their article. Do not remove it as a leftover.
 */
const TRANSLITERATIONS = {
    'æ': 'ae', // ae ligature
    'ø': 'oe', // o with stroke
    'å': 'aa', // a with ring
    'ä': 'ae', // a with diaeresis
    'ö': 'oe', // o with diaeresis
    'ü': 'ue', // u with diaeresis
    'ß': 'ss', // sharp s
    'đ': 'd', // d with stroke
    'ð': 'd', // eth
    'þ': 'th', // thorn
    'ł': 'l', // l with stroke
    'ñ': 'n', // n with tilde
    'ç': 'c', // c with cedilla
    '&': ' and ',
    '@': ' at ',
    '€': ' eur ', // euro sign
    '+': ' plus ',
};
export function toSlug(input, maxLength = SLUG_MAX_LENGTH) {
    const lowered = (input ?? '').toLowerCase();
    let expanded = '';
    for (const char of lowered) {
        expanded += TRANSLITERATIONS[char] ?? char;
    }
    const ascii = expanded
        // Decompose, then drop every combining mark, so "e" with an acute becomes
        // "e" rather than being replaced by a hyphen below.
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-+|-+$/g, '');
    if (ascii.length <= maxLength)
        return ascii;
    // Trim on a hyphen boundary so the slug does not end mid-word.
    const clipped = ascii.slice(0, maxLength);
    const lastHyphen = clipped.lastIndexOf('-');
    return (lastHyphen > maxLength * 0.6 ? clipped.slice(0, lastHyphen) : clipped).replace(/-+$/, '');
}
export function isValidSlug(slug, maxLength = SLUG_MAX_LENGTH) {
    return (typeof slug === 'string' &&
        slug.length > 0 &&
        slug.length <= maxLength &&
        SLUG_PATTERN.test(slug));
}
/** The message shown next to a rejected slug field. */
export function describeSlugRequirement() {
    return 'Lowercase letters a-z, digits and hyphens only.';
}
/**
 * Appends "-2", "-3", ... until the slug is not in `taken`. Used to propose a
 * slug the unique constraint will accept rather than round-tripping a failure.
 */
export function uniqueSlug(candidate, taken, maxLength = SLUG_MAX_LENGTH) {
    const used = new Set([...taken]);
    if (!used.has(candidate))
        return candidate;
    for (let suffix = 2; suffix < 1000; suffix += 1) {
        const tail = `-${suffix}`;
        const head = candidate.slice(0, Math.max(1, maxLength - tail.length)).replace(/-+$/, '');
        const next = `${head}${tail}`;
        if (!used.has(next))
            return next;
    }
    return candidate;
}
/**
 * GitHub-style anchor id for a heading, so `[[kb:article#permissions]]` can
 * point at a heading. The web renderer stamps ids on headings with this same
 * function, which is the only thing that keeps the two ends in agreement.
 */
export function toHeadingId(headingText) {
    return (headingText ?? '')
        .trim()
        .toLowerCase()
        .replace(/[^\p{L}\p{N}\s-]/gu, '')
        .replace(/\s+/g, '-');
}
//# sourceMappingURL=slug.js.map